// Utility functions
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combine class names with Tailwind CSS merge
 * @param {...any} inputs - Class names to combine
 * @returns {string} - Combined class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Get full image URL by prepending backend URL to relative paths
 * @param {string} imageUrl - The image URL (can be relative or absolute)
 * @returns {string} - Full image URL
 */
export function getImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return '';
  }

  let rawUrl = imageUrl.trim();
  if (!rawUrl) return '';

  // Handle accidental JSON-stringified values: '["/uploads/..."]'
  if ((rawUrl.startsWith('["') && rawUrl.endsWith('"]')) || (rawUrl.startsWith("['") && rawUrl.endsWith("']"))) {
    try {
      const parsed = JSON.parse(rawUrl.replace(/'/g, '"'));
      if (Array.isArray(parsed) && typeof parsed[0] === 'string') rawUrl = parsed[0].trim();
    } catch (_e) {
      // Ignore parse errors and continue with original value
    }
  }

  const envBackendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const defaultBackend = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000';
  const normalizedBackendBase = (envBackendUrl || defaultBackend)
    .replace(/\/+$/, '')
    .replace(/\/api$/i, '');

  let finalUrl = rawUrl;

  // Handle localhost URLs that might be stored in the database
  if (rawUrl.includes('localhost:8000') || rawUrl.includes('127.0.0.1:8000')) {
    finalUrl = rawUrl
      .replace('http://localhost:8000', normalizedBackendBase)
      .replace('http://127.0.0.1:8000', normalizedBackendBase);
    return finalUrl;
  }

  // If it's already a full URL, return as is
  const lowerUrl = rawUrl.toLowerCase();
  if (
    lowerUrl.startsWith('http://') ||
    lowerUrl.startsWith('https://') ||
    lowerUrl.startsWith('//') ||
    lowerUrl.startsWith('data:') ||
    lowerUrl.startsWith('blob:')
  ) {
    return rawUrl;
  }

  // If it's a relative URL starting with /uploads, prepend backend URL
  if (rawUrl.startsWith('/uploads/')) {
    finalUrl = `${normalizedBackendBase}${rawUrl}`;
    return finalUrl;
  }

  // Handle API-prefixed upload paths
  if (rawUrl.startsWith('/api/uploads/')) {
    finalUrl = `${normalizedBackendBase}${rawUrl.replace(/^\/api/i, '')}`;
    return finalUrl;
  }
  if (rawUrl.startsWith('api/uploads/')) {
    finalUrl = `${normalizedBackendBase}/${rawUrl.replace(/^api\//i, '')}`;
    return finalUrl;
  }

  // Handle paths missing the leading slash (uploads/...)
  if (rawUrl.startsWith('uploads/')) {
    finalUrl = `${normalizedBackendBase}/${rawUrl}`;
    return finalUrl;
  }

  // Handle folder-relative keys from backend/storage: products/..., branding/...
  if (/^(products|categories|banners|branding|catalog|general|returns)\//i.test(rawUrl)) {
    finalUrl = `${normalizedBackendBase}/uploads/${rawUrl}`;
    return finalUrl;
  }

  // For legacy data (just filenames), try multiple folders in order of priority
  // This is a temporary fix until migration is run
  finalUrl = `${normalizedBackendBase}/uploads/general/${rawUrl}`;
  return finalUrl;
}

/**
 * Format currency value
 * @param {number} amount - The amount to format
 * @returns {string} - Formatted currency string
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export function formatDate(date) {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
}

/**
 * Format date and time
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date and time string
 */
export function formatDateTime(date) {
  return new Intl.DateTimeFormat('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

/**
 * Truncate text to specified length
 * @param {string} text - The text to truncate
 * @param {number} length - Maximum length
 * @returns {string} - Truncated text
 */
export function truncateText(text, length = 100) {
  if (!text || text.length <= length) return text;
  return text.substring(0, length) + '...';
}

/**
 * Generate a random ID
 * @returns {string} - Random ID
 */
export function generateId() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if a string is a valid email
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether email is valid
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Check if a string is a valid phone number (Indian format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - Whether phone number is valid
 */
export function isValidPhone(phone) {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone);
}

/**
 * Get initials from a name
 * @param {string} name - Full name
 * @returns {string} - Initials
 */
export function getInitials(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
}

/**
 * Calculate discount percentage
 * @param {number} mrp - Maximum Retail Price
 * @param {number} sellingPrice - Selling Price
 * @returns {number} - Discount percentage
 */
export function calculateDiscount(mrp, sellingPrice) {
  if (!mrp || !sellingPrice || mrp <= sellingPrice) return 0;
  return Math.round(((mrp - sellingPrice) / mrp) * 100);
}

/**
 * Get status color class
 * @param {string} status - Status string
 * @returns {string} - CSS class for status color
 */
export function getStatusColor(status) {
  const statusColors = {
    active: 'text-green-500',
    inactive: 'text-gray-500',
    pending: 'text-yellow-500',
    approved: 'text-green-500',
    rejected: 'text-red-500',
    processing: 'text-blue-500',
    shipped: 'text-purple-500',
    delivered: 'text-green-500',
    cancelled: 'text-red-500',
    returned: 'text-orange-500',
  };

  return statusColors[status?.toLowerCase()] || 'text-gray-500';
}
